import React from 'react';
import { Target, Users, Award, TrendingUp } from 'lucide-react';
import { companyInfo, facilityImages } from '../utils/mockData';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About Us</h1>
            <p className="text-xl text-slate-300">
              Professional electrical solutions with a commitment to quality, safety, and reliability
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Perfect Electrical Solutions
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Established in 2024, <strong>Perfect Electrical Solutions</strong> has quickly become a trusted name in industrial electrical and automation solutions across Madhya Pradesh. Based in Pithampur, we serve manufacturing units, commercial buildings, and industrial plants with professional expertise.
                </p>
                <p>
                  Under the leadership of <strong>Nilesh Kumawat</strong>, our team specializes in Control Panel Manufacturing, PLC Programming, VFD Solutions, HMI Systems, and comprehensive repair services. We don't just provide products – we deliver complete solutions tailored to your specific needs.
                </p>
                <p>
                  Our commitment is simple: provide reliable electrical solutions that enhance productivity, ensure safety, and deliver long-term value. Every project we undertake reflects our dedication to quality workmanship and customer satisfaction.
                </p>
                <p>
                  We understand that downtime costs money. That's why we offer quick response times, expert diagnostics, and efficient solutions that get your operations back on track fast.
                </p>
              </div>

              <div className="mt-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600 mb-2">Company CEO</p>
                <p className="text-2xl font-bold text-slate-900">Nilesh Kumawat</p>
                <p className="text-slate-600 mt-2">Leading with technical expertise and commitment to excellence</p>
              </div>
            </div>

            <div className="space-y-4">
              <img
                src="https://images.unsplash.com/photo-1580983230786-ce385a434707"
                alt="Electrical Engineer at Work"
                className="rounded-xl shadow-lg w-full h-64 object-cover"
              />
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e"
                  alt="Industrial Automation"
                  className="rounded-xl shadow-lg w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1565608438257-fac3c27beb36"
                  alt="Testing Equipment"
                  className="rounded-xl shadow-lg w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Clients Trust Us
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our values drive everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Technical Excellence</h3>
              <p className="text-slate-600 text-sm">
                Expert knowledge in PLC, VFD, HMI systems and industrial automation
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Quality First</h3>
              <p className="text-slate-600 text-sm">
                Premium components, proper documentation, and rigorous testing
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-orange-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Client Focused</h3>
              <p className="text-slate-600 text-sm">
                Understanding your needs and delivering customized solutions
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-purple-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Reliable Support</h3>
              <p className="text-slate-600 text-sm">
                24/7 emergency support and comprehensive AMC services
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Images */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Facility
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Well-equipped manufacturing unit with modern tools and testing equipment
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {facilityImages.map((facility) => (
              <div key={facility.id} className="group relative overflow-hidden rounded-xl shadow-lg">
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{facility.title}</h3>
                    <p className="text-slate-200 text-sm">{facility.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Facts */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-5xl font-bold mb-2">{companyInfo.yearsOfExperience}+</p>
              <p className="text-blue-100">Years in Business</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">{companyInfo.projectsCompleted}+</p>
              <p className="text-blue-100">Projects Completed</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">{companyInfo.clientsServed}+</p>
              <p className="text-blue-100">Happy Clients</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">10</p>
              <p className="text-blue-100">Team Members</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
